import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { ACCENT_GRADIENT_NAVY, ACCENT_GRADIENT_RED } from './eleganceConstants';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  isChunkError: boolean;
}

function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return (
      msg.includes('loading chunk') ||
      msg.includes('loading css chunk') ||
      msg.includes('dynamically imported module') ||
      msg.includes('failed to fetch') ||
      msg.includes('importing a module script failed')
    );
  }
  return false;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return {
      hasError: true,
      isChunkError: isChunkLoadError(error),
    };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, info);

    // Auto-reload once for chunk load errors (stale deployment)
    if (isChunkLoadError(error)) {
      const reloadedKey = 'error_boundary_reloaded';
      if (!sessionStorage.getItem(reloadedKey)) {
        sessionStorage.setItem(reloadedKey, '1');
        window.location.reload();
        return;
      }
      sessionStorage.removeItem(reloadedKey);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Flat accent (constants are flat colors now — gradients retired).
      const accent = this.state.isChunkError ? ACCENT_GRADIENT_NAVY : ACCENT_GRADIENT_RED;
      return (
        <div className="min-h-[60vh] flex items-center justify-center px-4 py-12">
          <div className="max-w-lg w-full bg-card border border-border rounded-lg shadow-sm overflow-hidden">
            <div className="h-1 w-full" style={{ background: accent }} />
            <div className="p-8 text-center">
              <AlertTriangle
                className="w-8 h-8 mb-4 mx-auto"
                style={{ color: accent }}
                strokeWidth={1.25}
                aria-hidden="true"
              />
              <h2 className="font-serif text-xl text-foreground mb-2">
                {this.state.isChunkError ? 'App Updated' : 'Something went wrong'}
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                {this.state.isChunkError
                  ? 'A new version of the app has been deployed. Reload to get the latest version.'
                  : 'An unexpected error occurred. Reload to try again, or return to the dashboard.'}
              </p>
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={this.handleReload}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
                >
                  <RefreshCw className="w-4 h-4" />
                  Reload Page
                </button>
                {!this.state.isChunkError && (
                  <a
                    href="/"
                    className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium border border-border bg-card hover:bg-accent/50 transition-colors"
                  >
                    Back to Home
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
